// حواله پرداخت اعتبارات (هزینه‌ای یا تملک دارایی‌های سرمایه‌ای)
// programType: 0 هزینه‌ای ، 1 تملک
// tab: 'budget' از تب بودجه (حواله پرداخت) ، 'credit' از تب اعتبارات (تامین اعتبار حواله‌ها و ثبت تنخواه)
//   در تب اعتبارات فقط حواله‌های ارسال‌شده به اعتبارات دیده می‌شوند، حوالهٔ جدید فقط «پرداخت تنخواه»
//   است و فقط همین نوع حذف می‌شود. همهٔ این قیدها در سرور هم اعمال می‌شوند.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTabs } from '../TabManager';
import ListPage, { ui } from '../../components/ListPage';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import BudgetTamalokHavalehDetail from './BudgetTamalokHavalehDetail';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng  = s => String(s ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const fmtNum = n => n ? Number(n).toLocaleString('fa-IR') : '•';

const KINDS = ['هزینه قطعی', 'تسویه تنخواه', 'پرداخت تنخواه', 'برگشت تنخواه', 'پیش پرداخت', 'علی الحساب'];
const TITLES = {
  budget: ['حواله پرداخت اعتبارات هزینه ای', 'حواله پرداخت اعتبارات تملک داراییهای سرمایه ای'],
  credit: ['تامین اعتبار حواله های اعتبارات هزینه ای و ثبت تنخواه',
           'تامین اعتبار حواله های اعتبارات تملک داراییهای سرمایه ای و ثبت تنخواه'],
};
const PAY_FUND = 2;   // پرداخت تنخواه
const OFF = { background: '#f0f0f0', color: '#999', cursor: 'not-allowed' };

const EMPTY = {
  havaleh_no: '', request_no: '', request_date: '', havaleh_date: '', kind: 0,
  letter_no: '', letter_date: '', remark: '', send_to_accounting: false, send_to_credit: false,
};

const API = (path, opts = {}) => fetch(`/api/budget${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

export default function BudgetTamalokHavaleh({ user, onBack, programType = 1, tab = 'budget' }) {
  const isCredit = tab === 'credit';
  const title = (TITLES[tab] || TITLES.budget)[programType === 0 ? 0 : 1];
  // حالت نهایی از سرور (کاربر طراح یا بعضی دستگاه‌ها هر دو کار را با هم دارند)
  const [mode, setMode] = useState(tab);
  const credit = mode === 'credit', budget = mode === 'budget';
  // مجوز دکمه‌های فرم FRMBUDGETHAVALEH — دیدن = دسترسی
  const vbf = useFormButtons('FRMBUDGETHAVALEH', ["BTNNEW","BTNEDIT","BTNDELETE","BTNHAVALEHBUDGETTRANSACTION","BTNOK"]);
  const curYear = new Date().toLocaleDateString('fa-IR').split('/')[0]
    .replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());

  const [year,   setYear]   = useState(curYear);
  const [search, setSearch] = useState('');
  const [data,     setData]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [toast,    setToast]    = useState('');
  const { openTab, goHome } = useTabs();

  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [hasMore,  setHasMore]  = useState(false);
  const [loadMore, setLoadMore] = useState(false);

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 3000); };

  const cols = ['شماره حواله', 'شماره تامین', 'تاریخ', 'نوع حواله', 'ریز', 'مبلغ حواله', 'تنخواه', 'پیش‌پرداخت', 'علی‌الحساب', 'جمع هزینه', 'کسورات', 'خالص', 'شرح'];
  const initW = [110, 90, 90, 110, 50, 120, 110, 110, 110, 120, 110, 120, 160];
  const [colW, setColW] = useState(initW);
  const colWRef = useRef(initW);
  useEffect(() => { colWRef.current = colW; }, [colW]);
  const startResize = useCallback((idx, e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sw = colWRef.current[idx];
    const onMove = ev => setColW(w => { const n = [...w]; n[idx] = Math.max(40, sw + (sx - ev.clientX)); return n; });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, []);

  const buildUrl = (yr, pg) => `/budget-havaleh/?year=${yr}&program_type=${programType}&tab=${tab}&search=${encodeURIComponent(search)}&page=${pg}`;

  const load = useCallback(async (yr = year) => {
    setLoading(true); setError(''); setPage(1); setSelected(null);
    const j = await API(buildUrl(yr, 1));
    setLoading(false);
    if (j.ok) { setData(j.data); setTotal(j.total || j.data.length); setHasMore(j.has_more || false); if (j.mode) setMode(j.mode); }
    else setError(j.msg);
  }, [year, search]);

  const loadNextPage = async () => {
    const next = page + 1; setLoadMore(true);
    const j = await API(buildUrl(year, next));
    setLoadMore(false);
    if (j.ok) { setData(d => [...d, ...j.data]); setHasMore(j.has_more || false); setPage(next); }
  };

  useEffect(() => { load(); }, []);

  const handleYear = e => {
    const v = toEng(e.target.value.replace(/[^0-9۰-۹]/g, '')).slice(0, 4);
    setYear(v);
    if (v.length === 4) load(v);
  };

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const openNew  = () => { if (!vbf.can('BTNNEW')) return;
    // در تب اعتبارات حوالهٔ جدید «پرداخت تنخواه» و ارسال‌شده به اعتبارات است
    setForm(credit ? { ...EMPTY, kind: PAY_FUND, send_to_credit: true } : EMPTY); setModal('new'); };
  // در تب اعتبارات فقط حوالهٔ پرداخت تنخواه حذف می‌شود
  const canDelete = vbf.can('BTNDELETE') && (!credit || (selected && selected.kind === PAY_FUND));
  const openEdit = () => { if (!vbf.can('BTNEDIT')) return;
    if (!selected) return;
    setForm({
      havaleh_no: selected.havaleh_no, request_no: selected.request_no, request_date: selected.request_date,
      havaleh_date: selected.havaleh_date, kind: selected.kind, letter_no: selected.letter_no,
      letter_date: selected.letter_date, remark: selected.remark,
      send_to_accounting: selected.send_to_accounting, send_to_credit: selected.send_to_credit,
    });
    setModal('edit');
  };
  const openDel = () => { if (!canDelete) return; if (selected) setModal('delete'); };

  const openDetail = () => { if (!vbf.can('BTNHAVALEHBUDGETTRANSACTION')) return;
    if (!selected) return;
    const h = selected;
    // در تب اعتبارات حوالهٔ بی‌قلم چیزی برای نمایش ندارد
    if (credit && !h.count) { showToast('برای این حواله قلمی ثبت نشده است'); return; }
    openTab(`${tab}-havaleh-detail:${h.id}`, `اقلام حواله ${toFa(h.request_no || h.havaleh_no)}`, '📑',
      () => <BudgetTamalokHavalehDetail user={user} onBack={goHome} havaleh={h} programType={programType} tab={tab} />);
  };

  const save = async () => {
    setSaving(true); setError('');
    const body = JSON.stringify({ ...form, year, program_type: programType, tab });
    const j = modal === 'new'
      ? await API('/budget-havaleh/create/', { method: 'POST', body })
      : await API(`/budget-havaleh/${selected.id}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast([j.msg, ...(j.notes || [])].join(' — ')); load(); }
    else setError(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await API(`/budget-havaleh/${selected.id}/delete/?tab=${tab}`, { method: 'DELETE' });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast('حذف شد'); load(); }
    else { setError(j.msg); setModal(j.used ? 'delete_blocked' : null); }
  };

  const toolbar = (
    <>
      <div style={ui.toolBtns}>
        {vbf.can('BTNNEW') && <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>}
        {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{ ...ui.btnEdit, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✎ ویرایش</button>}
        {vbf.can('BTNDELETE') && <button onClick={openDel} disabled={!canDelete} style={{ ...ui.btnDel, opacity: canDelete ? 1 : 0.4, cursor: canDelete ? 'pointer' : 'default' }}>✕ حذف</button>}
        {vbf.can('BTNHAVALEHBUDGETTRANSACTION') && <button onClick={openDetail} disabled={!selected} style={{ ...ui.btnItems, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>📑 اقلام حواله</button>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <label style={ui.flabel}>سال مالی</label>
        <input value={toFa(year)} onChange={handleYear} maxLength={4} placeholder="۱۴۰۵" style={ui.yearInput} />
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
        style={{ ...ui.searchInput, flex: 1, minWidth: '150px' }} placeholder="جستجو (شماره یا شرح)..." />
      {loading && <span>⏳</span>}
      {!loading && data.length > 0 && <div style={ui.countBadge}>{toFa(data.length)} از {toFa(total)} مورد</div>}
    </>
  );

  const footer = (hasMore && !search) ? (
    <button onClick={loadNextPage} disabled={loadMore} style={{
      padding: '9px 36px', background: loadMore ? '#ccc' : 'linear-gradient(135deg,#1a2f50,#1a237e)',
      color: '#fff', border: 'none', borderRadius: '8px', cursor: loadMore ? 'default' : 'pointer',
      fontFamily: 'inherit', fontSize: '13px', fontWeight: '700' }}>
      {loadMore ? '⏳ در حال بارگذاری...' : `▼ ۲۰۰ مورد بعدی (${toFa(String(total - data.length))} باقی‌مانده)`}
    </button>
  ) : null;

  return (
    <>
      <ListPage user={user} onBack={onBack} title={title} subtitle={isCredit ? 'بخش اعتبارات — سامانه ارجان' : 'بخش بودجه — سامانه ارجان'}
        toolbar={toolbar} error={!modal ? error : ''} footer={footer}>
        <table style={{ ...ui.table, tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              {cols.map((h, i) => (
                <th key={i} style={{ ...ui.th, width: colW[i], minWidth: 40, position: 'relative', userSelect: 'none' }}>
                  {h}<span onMouseDown={e => startResize(i, e)} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize' }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => {
              const sel = selected?.id === r.id;
              const bg  = sel ? '#1a2f50' : i % 2 === 0 ? '#fff' : '#fdf6e3';
              const tc  = sel ? '#fff' : '#111';
              const num = (v, color) => <td style={{ ...ui.tdNum, color: sel ? '#fff' : v ? color : '#ccc' }}>{fmtNum(v)}</td>;
              return (
                <tr key={r.id} style={{ background: bg, cursor: 'pointer' }}
                  onClick={() => setSelected(sel ? null : r)} onDoubleClick={() => { setSelected(r); setTimeout(openDetail, 0); }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#e8f0fe'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = bg; }}>
                  <td style={{ ...ui.td, fontWeight: '700', color: sel ? '#C8A84B' : '#1a237e' }}>{toFa(r.request_no) || '•'}</td>
                  <td style={{ ...ui.td, color: tc }}>{toFa(r.havaleh_no)}</td>
                  <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px', color: tc }}>{r.havaleh_date || '•'}</td>
                  <td style={{ ...ui.td, fontSize: '11px', color: tc }}>{r.kind_name}</td>
                  <td style={{ ...ui.td, color: tc }}>{toFa(r.count)}</td>
                  {num(r.amount, '#1b5e20')}
                  {num(r.fund_pay, '#e65100')}
                  {num(r.prepayment, '#6a1b9a')}
                  {num(r.on_account, '#6a1b9a')}
                  {num(r.hazineh, '#1a237e')}
                  {num(r.deduction, '#b71c1c')}
                  {num(r.net, '#1b5e20')}
                  <td style={{ ...ui.td, textAlign: 'right', fontSize: '11px', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.remark}>{r.remark || '•'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && data.length === 0 && <div style={ui.empty}>📭 داده‌ای یافت نشد</div>}
      </ListPage>

      {(modal === 'new' || modal === 'edit') && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '620px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? (credit ? '＋ پرداخت تنخواه جدید' : '＋ حواله جدید') : `✎ ویرایش حواله ${toFa(selected?.request_no || selected?.havaleh_no)}`}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              {error && <div style={ui.errBox}>⚠️ {error}</div>}
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>شماره حواله</label>
                  <input value={form.request_no} onChange={e => F('request_no', e.target.value)} style={ui.input} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>شماره تامین اعتبار (خالی = خودکار)</label>
                  <input type="number" value={form.havaleh_no} onChange={e => F('havaleh_no', e.target.value)} style={ui.input} />
                </div>
              </div>
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>تاریخ حواله</label>
                  {credit
                    ? <input value={form.request_date || (modal === 'new' ? 'امروز' : '')} disabled style={{ ...ui.input, ...OFF }} />
                    : <JalaliDatePicker value={form.request_date} onChange={v => F('request_date', v)} placeholder="تاریخ حواله" />}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>تاریخ تامین اعتبار</label>
                  {budget
                    ? <input value={form.havaleh_date} disabled style={{ ...ui.input, ...OFF }} />
                    : <JalaliDatePicker value={form.havaleh_date} onChange={v => F('havaleh_date', v)} placeholder="تاریخ تامین اعتبار" />}
                </div>
              </div>
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>نوع حواله</label>
                  <select value={form.kind} disabled={credit} onChange={e => F('kind', Number(e.target.value))} style={{ ...ui.input, ...(credit ? OFF : {}) }}>
                    {KINDS.map((o, i) => <option key={i} value={i}>{o}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>شماره نامه</label>
                  <input value={form.letter_no} onChange={e => F('letter_no', e.target.value)} style={ui.input} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>تاریخ نامه</label>
                  <JalaliDatePicker value={form.letter_date} onChange={v => F('letter_date', v)} placeholder="تاریخ نامه" />
                </div>
              </div>
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>شرح</label>
                  <input value={form.remark} onChange={e => F('remark', e.target.value)} style={ui.input} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: '#444', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.send_to_accounting} disabled={budget || (credit && modal === 'new')}
                    onChange={e => F('send_to_accounting', e.target.checked)} style={{ marginLeft: '8px' }} />
                  ارسال به حسابداری
                </label>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: '#444', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.send_to_credit} disabled={credit || modal === 'new'}
                    onChange={e => F('send_to_credit', e.target.checked)} style={{ marginLeft: '8px' }} />
                  ارسال به اعتبارات
                </label>
              </div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => { setModal(null); setError(''); }} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳ ...' : '💾 ذخیره'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#b71c1c,#c62828)' }}>
              <div style={ui.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '28px' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>🗑️</div>
              <div style={{ background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: '8px', padding: '10px 14px', fontWeight: '700', color: '#b71c1c' }}>
                حواله شماره {toFa(selected?.request_no || selected?.havaleh_no)}
              </div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setModal(null)} style={ui.btnCancel}>خیر</button>
              {canDelete && <button onClick={doDelete} disabled={saving} style={{ ...ui.btnSave, background: '#b71c1c' }}>{saving ? '⏳...' : 'بله، حذف'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete_blocked' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#e65100,#f57c00)' }}>
              <div style={ui.mTitle}>🚫 عدم امکان حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '26px', fontWeight: '700', color: '#1a2f50', lineHeight: '1.8' }}>{error}</div>
            <div style={ui.mFooter}><button onClick={() => { setModal(null); setError(''); }} style={ui.btnSave}>باشه</button></div>
          </div>
        </div>
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}
